import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { DocsVerificationLivenessTagErrorEnum } from '@models/enums';
import { DocsVerificationTabEnum, DocsVerificationTypeEnum } from '@models/enums';
import type { FaceDetectResult, FileWithPreview, ThemeProps } from '@models/types';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { RequirementTypeEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/requirements/types';
import { Info } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/info';
import { Success } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/success';
import type { BiometricsProps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/types';
import { SelfieStepEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/types';
import { useMobileHeight } from '@modules/docs-verification-widget/hooks/use-mobile-height';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import cn from 'classnames';

import { Timer } from '../../docs-verification-steps/start/timer';
import { usePageTitle } from '../../hooks/use-page-title';
import { useUploadFile } from '../../hooks/use-upload-file';
import { MobileHeader } from '../../mobile-header';
import { Requirements } from '../../requirements';
import { STEPS_MOBILE_TITLE } from '../constants';

import { Liveness } from './liveness';
import { SelfieContainer } from './styles';

export const Selfie = ({ onSubmit, isLoading }: BiometricsProps) => {
    const files = useSelector((state: RootState) => state.docsVerification.documents.files).filter(
        (item) => item.tab === DocsVerificationTabEnum.BIOMETRICS,
    );
    const [isFailedVerification, setIsFailedVerification] = useState(false);
    const [step, setStep] = useState(() =>
        files[0]?.status?.success ? SelfieStepEnum.SUCCESS : SelfieStepEnum.INFO,
    );
    const { scrollTop } = useWidgetContext();
    const dispatch = useDispatch();
    const theme = useTheme<ThemeProps>();
    const isCameraMode = useSelector((state: RootState) => state.docsVerification.isCameraMode);

    const isShowRequirements = useSelector(
        (state: RootState) => state.docsVerification.isShowRequirements,
    );
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const { uploadFile } = useUploadFile();
    const { minHeight } = useMobileHeight();
    const { pageTitle } = usePageTitle();

    const handleFilesChange = useCallback(
        async (
            file: FileWithPreview | FileWithPreview[],
            tag?: DocsVerificationLivenessTagErrorEnum,
            faceDetectResult?: FaceDetectResult,
        ) =>
            uploadFile({
                file,
                documentType: DocsVerificationTypeEnum.LIVENESS,
                tab: DocsVerificationTabEnum.BIOMETRICS,
                faceDetectResult,
            }),
        [uploadFile],
    );

    const handleBackClick = () => {
        if (isCameraMode) {
            dispatch(docsVerificationSlice.actions.setIsCameraMode(false));
        }

        if (isShowRequirements) {
            dispatch(docsVerificationSlice.actions.setIsShowRequirements(false));
        } else if (step === SelfieStepEnum.LIVENESS) {
            setStep(SelfieStepEnum.INFO);
        } else {
            dispatch(docsVerificationSlice.actions.goBackStep());
        }

        setTimeout(() => {
            scrollTop();
        });
    };

    const stepsMobileTitleNode = window.document.getElementById(STEPS_MOBILE_TITLE);

    const handleSubmit = useCallback(
        async (status?: boolean) => {
            setIsFailedVerification(!!status);

            return onSubmit(status);
        },
        [onSubmit],
    );

    const handleClose = useCallback(() => {
        if (step === SelfieStepEnum.LIVENESS || step === SelfieStepEnum.SUCCESS) {
            setStep(SelfieStepEnum.INFO);

            return;
        }
        dispatch(docsVerificationSlice.actions.goBackStep());
    }, [dispatch, step]);

    return (
        <SelfieContainer
            style={{
                minHeight,
            }}
        >
            {matchesMobileSm &&
                !isShowRequirements &&
                stepsMobileTitleNode &&
                createPortal(
                    <React.Fragment>
                        <MobileHeader
                            onBack={handleBackClick}
                            title={pageTitle}
                            className={cn({ 'selfie-fixed-header': isCameraMode })}
                            isFixed={isCameraMode}
                        />
                        {!isCameraMode && <Timer isHidden={true} />}
                    </React.Fragment>,
                    stepsMobileTitleNode,
                )}

            {isShowRequirements ? (
                <Requirements type={RequirementTypeEnum.SELFIE} onBack={handleBackClick} />
            ) : (
                <React.Fragment>
                    {step === SelfieStepEnum.INFO && (
                        <Info
                            onSubmit={() => {
                                setStep(SelfieStepEnum.LIVENESS);
                            }}
                        />
                    )}
                    {step === SelfieStepEnum.LIVENESS && (
                        <div className={cn('main', 'main-liveness')}>
                            <Liveness
                                onFileChange={handleFilesChange}
                                onSubmit={handleSubmit}
                                onClose={handleClose}
                            />
                        </div>
                    )}
                    {step === SelfieStepEnum.SUCCESS && (
                        <Success
                            onSubmit={() => onSubmit(isFailedVerification)}
                            isLoading={isLoading}
                        />
                    )}
                </React.Fragment>
            )}
        </SelfieContainer>
    );
};
